// 公開API（ARCHITECTURE.md の battle 節）。
// 流れ：createBattle → advance（プレイヤーの手番まで進める）→ apply（1コマンド）→ advance → … → isOver / result。
// 自分の手番は「roll → move → build（任意・無料）→ attack / potion / wait」。
import type { BattleResult, BattleSetup, BuildingType, Command, ItemId, Side, StatusKey, TargetRef } from '../types';
import { BUILDING_TYPES, STATUS_KEYS, ITEM_IDS } from '../types';
import { CELLS } from './hex';
import type { Actor, Battle } from './state';
import type { BattleEvent } from './events';
import { CFG } from './config';
import * as R from './rules';
import { decide, type AiOptions } from './ai';
import { rivalAiType } from './stages';
import { createBattleState } from './setup';
import { ENEMIES, ENEMY_TYPE_INFO, type EnemyType } from '../data/enemies';

export type { Battle } from './state';

/** 戦闘を作る（まだ誰の手番でもない。advance で最初の自分の手番まで進める） */
export function createBattle(setup: BattleSetup): Battle {
  return createBattleState(setup);
}

export const isOver = (b: Battle) => R.over(b);

/** advance 1回で回る手番の上限（自分の手番までに全員が何度か動いても数百。これを超えたら進行の不具合として打ち切る） */
const ADVANCE_GUARD = 20000;

/** 自分のヒーローの入力待ちになるまで、敵ヒーロー・中立・ガーディアンの手番を進める */
export function advance(b: Battle): BattleEvent[] {
  const ev: BattleEvent[] = [];
  let guard = 0;
  while (!R.over(b)) {
    // 手番が進まない状態（時刻が進まない等）で回り続けないように、手番探しも数える。
    // サーバーの検証ではイベントが溜まり続けるとメモリの上限で落ちる（503）ので、上限は低めに
    if (++guard > ADVANCE_GUARD) {
      R.finish(b, null, 'timeout', ev);
      break;
    }
    if (b.current == null) {
      if (!R.nextTurn(b, ev)) break;
      continue;
    }
    const a = R.currentActor(b)!;
    if (a.kind === 'hero') {
      if (a.side === 'blue') break;
      runAiTurn(b, a, ev, rivalOpts(b));
    } else R.neutralTurn(b, ev);
  }
  return ev;
}

/** 敵の武将の立ち回り（戦場の傾向。無ければ通常のAI） */
function rivalOpts(b: Battle): AiOptions {
  const t = rivalAiType(b.setup.stageId, R.rulesOf(b));
  return t ? { style: t.style, aggro: t.aggro } : {};
}

function runAiTurn(b: Battle, a: Actor, ev: BattleEvent[], opts: AiOptions = {}) {
  for (let k = 0; k < 20 && b.current === a.id && !R.over(b); k++) {
    const r = execute(b, decide(b, opts));
    if (r.ok) ev.push(...r.events);
    else {
      const w = R.cmdWait(b);
      if (w.ok) ev.push(...w.events);
    }
  }
  if (b.current === a.id && !R.over(b)) {
    const w = R.cmdWait(b);
    if (w.ok) ev.push(...w.events);
  }
}

const isInt = (v: unknown): v is number => Number.isInteger(v);
const validCell = (v: unknown): v is number => isInt(v) && v >= 0 && v < CELLS.length;

/** コマンドの形の検証（サーバーに届くJSONを信用しない） */
export function commandShapeError(cmd: unknown): string | null {
  if (!cmd || typeof cmd !== 'object') return 'shape';
  const c = cmd as Record<string, unknown>;
  switch (c.t) {
    case 'roll':
    case 'potion':
    case 'wait':
      return null;
    case 'item':
      return ITEM_IDS.includes(c.id as ItemId) ? null : 'shape';
    case 'move':
      if (!validCell(c.to)) return 'shape';
      if (c.route !== undefined && (!Array.isArray(c.route) || c.route.length > CELLS.length || !c.route.every(validCell))) return 'shape';
      return null;
    case 'attack': {
      const t = c.target as Record<string, unknown> | undefined;
      if (!t || typeof t !== 'object') return 'shape';
      if (t.kind === 'actor' ? !isInt(t.id) : t.kind === 'core' ? t.side !== 'blue' && t.side !== 'red' : true) return 'shape';
      if (c.skill !== undefined && typeof c.skill !== 'boolean') return 'shape';
      if (c.ougi !== undefined && typeof c.ougi !== 'boolean') return 'shape';
      return null;
    }
    case 'build':
      if (!BUILDING_TYPES.includes(c.type as BuildingType) || !validCell(c.cell)) return 'shape';
      return null;
    case 'demolish':
      return validCell(c.cell) ? null : 'shape';
    case 'cast':
      if (c.cell !== undefined && !validCell(c.cell)) return 'shape';
      if (c.ougi !== undefined && typeof c.ougi !== 'boolean') return 'shape';
      return null;
    default:
      return 'shape';
  }
}

/** 現在の手番のヒーローに1コマンドを実行する（自分・敵の共通経路） */
function execute(b: Battle, cmd: Command): R.CmdResult {
  const shape = commandShapeError(cmd);
  if (shape) return R.fail(shape);
  switch (cmd.t) {
    case 'roll':
      return R.cmdRoll(b);
    case 'move':
      return R.cmdMove(b, cmd.to, cmd.route);
    case 'attack':
      return R.cmdAttack(b, cmd.target, !!cmd.skill || !!cmd.ougi, !!cmd.ougi);
    case 'build':
      return R.cmdBuild(b, cmd.type, cmd.cell);
    case 'demolish':
      return R.cmdDemolish(b, cmd.cell);
    case 'cast':
      return R.cmdCast(b, cmd.cell, !!cmd.ougi);
    case 'potion':
      return R.cmdPotion(b);
    case 'item':
      return R.cmdItem(b, cmd.id);
    case 'wait':
      return R.cmdWait(b);
  }
}

/** プレイヤーの1コマンド。自分の手番でなければ先に advance する（その分のイベントも返す） */
export function apply(b: Battle, cmd: Command): { ok: true; events: BattleEvent[] } | { ok: false; error: string } {
  if (R.over(b)) return { ok: false, error: 'over' };
  const shape = commandShapeError(cmd);
  if (shape) return { ok: false, error: shape };
  const pre = b.current == null ? advance(b) : [];
  if (R.over(b)) return { ok: false, error: 'over' };
  const a = R.currentActor(b);
  if (!a || a.kind !== 'hero' || a.side !== 'blue') return { ok: false, error: 'turn' };
  const r = execute(b, cmd);
  if (!r.ok) return r;
  // 防衛戦：この操作で最後の攻め手を倒したら、そこで勝ち
  if (b.defense) R.checkDefenseWin(b, r.events);
  b.log.push(JSON.parse(JSON.stringify(cmd)));
  return { ok: true, events: [...pre, ...r.events] };
}

/** 自分のヒーローの次の1手（オート・掃討・シミュレーター）。自分の手番でなければ wait を返す */
export function autoCommand(b: Battle, opts: AiOptions = {}): Command {
  const a = R.currentActor(b);
  if (!a || a.kind !== 'hero' || a.side !== 'blue') return { t: 'wait' };
  return decide(b, opts);
}

export function result(b: Battle): BattleResult {
  const over = R.over(b);
  const win = over && b.winner === 'blue';
  const relays = b.tiles.filter((t) => t.relay);
  const allRelaysOwned = relays.length > 0 && relays.every((t) => t.owner === 'blue');
  const s = b.stats;
  return {
    win,
    reason: over ? (b.reason ?? 'lost') : 'invalid',
    playerTurns: s.playerTurns,
    ticks: b.time,
    heroKOs: s.heroKOs,
    allRelaysOwned,
    stars: [win, win && s.heroKOs === 0, win && allRelaysOwned],
    kills: { ...s.kills },
    bossKilled: s.bossKilled,
    enemyBuildingsDestroyed: s.enemyBuildingsDestroyed,
    buildingsBuilt: s.buildingsBuilt,
    chests: [...(s.chests ?? [])],
    surge: { blue: R.surgeOf(b, 'blue').stage, red: R.surgeOf(b, 'red').stage },
    surgePoints: { blue: R.surgeOf(b, 'blue').points, red: R.surgeOf(b, 'red').points },
  };
}

function invalidResult(b: Battle): BattleResult {
  return { ...result(b), win: false, reason: 'invalid', stars: [false, false, false] };
}

/** サーバー検証：setup と commands を再生する。不正なコマンド・決着後のコマンド・決着しない列は reason:'invalid' */
export function replay(setup: BattleSetup, commands: Command[]): BattleResult {
  let b: Battle;
  try {
    b = createBattle(setup);
  } catch {
    return {
      win: false,
      reason: 'invalid',
      playerTurns: 0,
      ticks: 0,
      heroKOs: 0,
      allRelaysOwned: false,
      stars: [false, false, false],
      kills: {},
      bossKilled: false,
      enemyBuildingsDestroyed: 0,
      buildingsBuilt: 0,
    };
  }
  if (!Array.isArray(commands)) return invalidResult(b);
  for (const cmd of commands) {
    advance(b);
    if (R.over(b)) return invalidResult(b);
    const r = apply(b, cmd);
    if (!r.ok) return invalidResult(b);
  }
  advance(b);
  if (!R.over(b)) return invalidResult(b);
  return result(b);
}

/** 全自動の1戦（掃討・シミュレーター） */
export function simulate(setup: BattleSetup, opts: AiOptions = {}): { result: BattleResult; commands: Command[]; battle: Battle } {
  const b = createBattle(setup);
  for (let guard = 0; guard < 100000; guard++) {
    advance(b);
    if (R.over(b)) break;
    let r = apply(b, autoCommand(b, opts));
    if (!r.ok) r = apply(b, { t: 'wait' });
    if (!r.ok) break;
  }
  return { result: result(b), commands: b.log.slice(), battle: b };
}

// ---------- 画面用の判定 ----------

export const currentActor = (b: Battle) => R.currentActor(b);
export const actorById = (b: Battle, id: number) => R.actorById(b, id);
export const playerHero = (b: Battle) => R.heroOf(b, 'blue')!;
/** 敵ヒーロー（防衛戦にはいない） */
export const rivalHero = (b: Battle) => R.heroOf(b, 'red')!;

export interface DefenseInfo {
  /** 来た波の数（0＝準備中） */
  wave: number;
  total: number;
  /** 次の波まであと何手番（自分の手番で数える。最後の波が来たら null） */
  nextIn: number | null;
  /** 残っている攻め手の数 */
  attackers: number;
  /** ルールの説明（開始時に出す） */
  name: string;
  desc: string;
}
/** 防衛戦の状況（通常の戦いなら null） */
/** 砦（章の9番目・rules 3）の手番の上限：上限と残り（自分の手番で数える。上限のない戦いは null） */
export function turnLimitInfo(b: Battle): { limit: number; left: number } | null {
  if (!R.castleTurnLimited(b)) return null;
  const limit = R.turnLimitOf(b);
  return { limit, left: Math.max(0, limit - Math.max(1, b.stats.playerTurns) + 1) };
}

/** 自本陣の守り（鬼の棟梁・rules 3）の被ダメ倍率（守りが無ければ 1） */
export const coreGuardMult = (b: Battle, side: 'blue' | 'red') => R.coreGuardMult(b, side);

export function defenseInfo(b: Battle): DefenseInfo | null {
  const D = b.defense;
  if (!D) return null;
  const next = D.wave < D.total ? R.defenseWaveTurn(D.wave + 1) : null;
  return {
    wave: D.wave,
    total: D.total,
    nextIn: next == null ? null : Math.max(0, next - Math.max(1, b.stats.playerTurns)),
    attackers: b.actors.filter((u) => u.alive && u.attacker).length,
    name: '防衛戦',
    desc: `${CFG.defense.prepTurns}手番の準備のあと、敵が${D.total}回に分けて本陣へ攻めてくる。波が来るたび、残っている野良の妖も寝返って加わる。第${D.total}波まで退ければ勝ち`,
  };
}
export const isPlayerTurn = (b: Battle) => {
  const a = R.currentActor(b);
  return !!a && a.kind === 'hero' && a.side === 'blue' && !R.over(b);
};

export interface LegalCommands {
  roll: boolean;
  move: boolean;
  attack: boolean;
  skill: boolean;
  /** 妖力の奥義を今放てて、届く相手がいる */
  ougi: boolean;
  /** 建てられる建物（理由つき。null なら建てられる） */
  build: Record<BuildingType, string | null>;
  potion: boolean;
  /** 今使えるアイテム */
  items: ItemId[];
  wait: boolean;
  /** 一撃離脱の移動中（move で動くか wait でやめる。攻撃・薬・建築はできない） */
  dashing: boolean;
  /** 自分を中心に放つ術を今放てる（縮地は跳べる先があるとき） */
  cast: boolean;
  /** 奥義版の術を今放てる */
  castOugi: boolean;
}

/** 今の自分の手番で押せる操作 */
export function legalCommands(b: Battle): LegalCommands {
  const mine = isPlayerTurn(b);
  const a = R.heroOf(b, 'blue')!;
  const act = mine && (b.phase === 'start' || b.phase === 'rolled' || b.phase === 'moved');
  const build = Object.fromEntries(BUILDING_TYPES.map((t) => [t, mine ? R.canBuildFor(b, a, t) : 'phase'])) as Record<BuildingType, string | null>;
  return {
    roll: mine && b.phase === 'start',
    move: mine && b.phase === 'rolled',
    attack: act && !a.dashing && R.attackTargetsAt(b, a, a.cell!, false).length > 0,
    skill: act && !a.dashing && R.skillReady(a) && R.attackTargetsAt(b, a, a.cell!, true).length > 0,
    ougi: act && !a.dashing && !R.ougiBlock(b, a) && R.castingOugi(a, () => R.attackTargetsAt(b, a, a.cell!, true)).length > 0,
    build,
    potion: act && !a.dashing && R.itemCount(a, 'heal') > 0 && a.hp < a.hpMax,
    items: mine ? ITEM_IDS.filter((id) => R.itemCount(a, id) > 0 && !R.itemBlock(b, a, id)) : [],
    dashing: mine && !!a.dashing,
    wait: act,
    cast: act && castReady(b, a, false),
    castOugi: act && castReady(b, a, true),
  };
}

/** 術を放てるか（縮地は跳べる先が1つでもあれば） */
function castReady(b: Battle, a: Actor, ougi: boolean): boolean {
  const j = R.castOf(a);
  if (!j) return false;
  if (castAims(a)) return castCells(b, ougi).length > 0;
  return !R.castBlock(b, a, ougi);
}
/** 自分を中心に放つ術（持っていなければ null）。kind で操作が変わる（leap は着地点を選ぶ） */
export function castInfo(b: Battle): { id: string; name: string; desc: string; ougiDesc: string; kind: string; aim: boolean } | null {
  const a = R.heroOf(b, 'blue');
  const j = R.castOf(a);
  return j && a ? { id: j.id, name: j.name, desc: j.desc, ougiDesc: j.ougiDesc, kind: j.kind, aim: castAims(a) } : null;
}
/** マスを選んで放つ術か（縮地＝着地点、送り火＝狙うマス） */
const castAims = (a: Actor) => R.castOf(a)?.kind === 'leap' || !!R.castPower(a, false)?.range;
/** マスを選んで放つ術の選べるマス（今の自分の手番で） */
export function castCells(b: Battle, ougi = false): number[] {
  const a = R.heroOf(b, 'blue');
  const p = a && R.castPower(a, ougi);
  if (!a || !p || !castAims(a)) return [];
  if (ougi ? R.ougiBlock(b, a) : !R.skillReady(a)) return [];
  return R.castOf(a)!.kind === 'leap' ? R.leapCells(b, a, p.radius) : R.aimCells(b, a, p.range ?? 0);
}
/** 術の範囲（今いるマスから。送り火は選んだマス cell から）。下見用 */
export function castArea(b: Battle, ougi = false, cell?: number): number[] {
  const a = R.heroOf(b, 'blue');
  const p = a && R.castPower(a, ougi);
  if (!a || !p || a.cell == null) return [];
  if (R.castOf(a)?.kind === 'leap') return cell != null ? [cell] : [];
  if (p.range) return cell != null ? R.CELLS_AROUND(cell, p.radius) : [];
  return R.CELLS_AROUND(a.cell, p.radius);
}

export interface ReachCell {
  cell: number;
  cost: number;
  prev: number | null;
  stop: boolean; // 止まれる（自分の建物は通れるが止まれない）
}
/** 賽を振った後の到達可能マス */
export function reachableCells(b: Battle): ReachCell[] {
  return (b.reach ?? []).map(([cell, cost, prev, stop]) => ({ cell, cost, prev: prev < 0 ? null : prev, stop: stop !== 0 }));
}
/** cell までの既定の経路（同じ費用なら塗るマスが多い経路） */
export function routeTo(b: Battle, cell: number): number[] {
  const a = R.currentActor(b);
  return a ? R.paintRoute(b, a, cell) : [];
}
/** なぞった経路が正しいか（partial=true なら途中＝止まれないマスで終わってもよい） */
export function validRoute(b: Battle, route: number[], partial = false): boolean {
  const a = R.currentActor(b);
  return !!a && a.kind === 'hero' && b.phase === 'rolled' && R.validPath(b, a, route, partial);
}
/** 自分のヒーローが今いるマスから攻撃できる相手（skill=true なら術の射程） */
export function attackTargets(b: Battle, skill = false, ougi = false): TargetRef[] {
  const a = R.heroOf(b, 'blue')!;
  if (a.cell == null) return [];
  if (ougi) return R.ougiBlock(b, a) ? [] : R.castingOugi(a, () => R.attackTargetsAt(b, a, a.cell!, true));
  if (skill && !R.skillReady(a)) return [];
  return R.attackTargetsAt(b, a, a.cell, skill);
}
/** 予測ダメージ（実際の攻撃と同じ計算）。attackerId 省略時は自分のヒーロー。敵ヒーローの id を渡せば被ダメ予測。ougi＝妖力の奥義 */
export function forecast(b: Battle, target: TargetRef, skill = false, attackerId?: number, ougi = false): R.Forecast | null {
  const a = attackerId != null ? R.actorById(b, attackerId) : R.heroOf(b, 'blue');
  if (!a || a.kind !== 'hero') return null;
  return R.forecastFor(b, a, target, skill || ougi, ougi);
}
/** 自分のヒーローの妖力の奥義（覚えていなければ null）。ready＝今放てる（妖力が最大・封印されていない）、block は放てないわけ */
export function ougiInfo(b: Battle): { name: string; desc: string; ready: boolean; block: string | null } | null {
  const a = R.heroOf(b, 'blue');
  const og = R.ougiOf(a);
  if (!a || !og) return null;
  const block = R.ougiBlock(b, a);
  return { name: a.spec?.ougiName ?? og.name, desc: og.desc, ready: !block, block };
}
/** ユニットの敵の型（重装・攻城）と鎧の残り・攻城の狙い */
export function unitEnemyType(b: Battle, id: number): { type: EnemyType; name: string; desc: string; armor?: number; armorMax?: number; siege?: Side } | null {
  const a = R.actorById(b, id);
  const type = a?.enemyId ? ENEMIES[a.enemyId]?.type : undefined;
  if (!a || !type) return null;
  if (type === 'siege' && !a.siegeTarget) return null; // キャンプの攻城の妖は普通の妖と同じ動き
  return { type, ...ENEMY_TYPE_INFO[type], ...(a.armorMax ? { armor: a.armor ?? 0, armorMax: a.armorMax } : {}), ...(a.siegeTarget ? { siege: a.siegeTarget } : {}) };
}
/** 建てられるマス */
export function buildableCells(b: Battle, type: BuildingType): number[] {
  const a = R.heroOf(b, 'blue')!;
  if (!isPlayerTurn(b)) return [];
  return R.canBuildFor(b, a, type) ? [] : R.buildCellsFor(b, a, type);
}
/** 建てられない理由（phase/locked/done/limit/mat/cells）。建てられるなら null */
export const buildCheck = (b: Battle, type: BuildingType) => R.canBuildFor(b, R.heroOf(b, 'blue'), type);
export const buildCostOf = (b: Battle, type: BuildingType, side: Side = 'blue') => R.buildCost(R.heroOf(b, side)!, type);
/** 建っている自分の建物の数と、同時に建てられる数。turn＝この手番に建てた数と、1手番に建てられる数 */
export const buildCount = (b: Battle, side: Side = 'blue') => {
  const a = R.heroOf(b, side);
  return {
    built: R.buildingsOf(b, side).length,
    max: a ? R.buildMaxCount(a) : 0,
    turnUsed: a?.builtThisTurn ?? 0,
    turnMax: a?.spec ? R.buildPerTurn(a) : 1,
  };
};
/** 建てた場合の性能（HP・効果・射程・図面Lv） */
export const buildingPreview = (b: Battle, type: BuildingType, side: Side = 'blue') => R.buildingStats(R.heroOf(b, side)!, type, b.cores[side].max);
export const materials = (b: Battle, side: Side = 'blue') => b.mat[side];
export const materialIncome = (b: Battle, side: Side = 'blue') => R.materialIncome(b, side);
export const turnOrder = (b: Battle, n = 6) => R.turnOrder(b, n);
export const territoryShare = (b: Battle, side: Side) => R.territoryShare(b, side);
export const territoryTier = (b: Battle, side: Side) => R.territoryTier(b, side);
/** 陣地ルール（名前・足元の効果・段階） */
export const territoryRule = (b: Battle) => R.territoryRule(b);
export const insideTerritory = (b: Battle, cell: number, side: Side) => R.insideTerritory(b, cell, side);
export const overtimeLevel = (b: Battle) => R.overtimeLevel(b);
export const wardOn = (b: Battle, side: Side) => R.wardOn(b, side);
/** 本陣への攻撃倍率（陣地段階・結界・長期戦。DEFを引いた後に掛かる分） */
export function coreAssaultMult(b: Battle, attacker: Side): number {
  const target: Side = R.enemyOf(attacker);
  return CFG.coreAssault[R.territoryTier(b, attacker)] * (R.wardOn(b, target) ? CFG.ward : 1) * (1 + CFG.overtime.add * R.overtimeLevel(b));
}
/** 術が使えるまでの自分の手番数（0 なら使える） */
export const skillWait = (b: Battle, id: number) => {
  const a = R.actorById(b, id);
  return a && a.kind === 'hero' ? Math.max(0, (a.skillReady ?? 0) - (a.turns ?? 0)) : 0;
};
/** ユニットの状態異常（名前・説明・残り手番）。表示用 */
export function unitStatus(b: Battle, id: number): { key: StatusKey; name: string; desc: string; good?: boolean; turns: number }[] {
  const a = R.actorById(b, id);
  if (!a?.status) return [];
  return STATUS_KEYS.filter((k) => R.hasStatus(a, k)).map((k) => ({ key: k, ...CFG.status.info[k], turns: a.status![k]!.turns }));
}
/** 自分のヒーローのアイテム（種類・名前・説明・残り・今使えないわけ） */
export function heroItems(b: Battle): { id: ItemId; name: string; desc: string; icon: string; count: number; block: string | null }[] {
  const a = R.heroOf(b, 'blue');
  if (!a) return [];
  const ids = a.items ? (Object.keys(a.items) as ItemId[]) : R.itemCount(a, 'heal') > 0 ? (['heal'] as ItemId[]) : [];
  return ids.map((id) => ({ id, ...CFG.items[id], ...(id === 'heal' ? { desc: healDesc(a) } : {}), count: R.itemCount(a, id), block: R.itemBlock(b, a, id) }));
}
/** 回復薬の説明：回復する量（呪いなら半分）と、今使ったときに実際に戻る量 */
function healDesc(a: Actor) {
  const amt = R.hasStatus(a, 'curse') ? Math.floor((a.spec?.potionHeal ?? 0) * CFG.status.curseHeal) : a.spec?.potionHeal ?? 0;
  const got = Math.max(0, Math.min(amt, a.hpMax - a.hp));
  return got > 0 && got < amt ? `HPを${amt}回復する（今は+${got}で満タン）` : `HPを${amt}回復する`;
}
/** アイテムの名前・説明 */
export const itemInfo = (id: ItemId) => ({ ...CFG.items[id] });
/** 状態異常の名前・説明 */
export const statusInfo = (key: StatusKey) => ({ ...CFG.status.info[key] });
/** 復活の地点（鳥居があればその隣） */
export const returnPoint = (b: Battle, side: Side = 'blue') => R.returnPoint(b, R.heroOf(b, side)!);

// ---------- 妖力（戦闘中の成長）：画面用 ----------

/** 妖力の表示名（CFG.surge.label。例「妖力」） */
export const surgeLabel = () => CFG.surge.label;
/** 段階の表示名（1→「壱」…）。範囲外は数字 */
export const surgeStageName = (stage: number) => CFG.surge.stageNames[stage - 1] ?? String(stage);
/** 最大段階（3） */
export const surgeMaxStage = () => R.surgeMax();
/** 陣営の妖力の段階（1..3） */
export const surgeStage = (b: Battle, side: Side = 'blue') => R.surgeOf(b, side).stage;
/** 陣営の累計の撃破ポイント */
export const surgePoints = (b: Battle, side: Side = 'blue') => R.surgeOf(b, side).points;
/** 次の段階までに要るポイント（最大段階なら null） */
export function surgeToNext(b: Battle, side: Side = 'blue'): number | null {
  const s = R.surgeOf(b, side);
  if (s.stage >= R.surgeMax()) return null;
  return Math.max(0, R.surgeThresholds(b, side)[s.stage] - s.points);
}
export interface SurgeInfo {
  label: string;
  stage: number;
  stageName: string;
  maxStage: number;
  points: number;
  /** 次の段階の必要な累計ポイント（最大なら null） */
  nextAt: number | null;
  /** 次の段階まであと何ポイント（最大なら null） */
  toNext: number | null;
  /** 今の段階の効果（atk・hp は割合、cd は術の待ちの増減、core は本陣への与ダメの上乗せ） */
  effect: { atk: number; hp: number; heal: number; cd: number; core: number };
  /** この陣営の段階ごとの必要ポイント（累計） */
  thresholds: number[];
}
/** 表示用にまとめたもの */
export function surgeInfo(b: Battle, side: Side = 'blue'): SurgeInfo {
  const s = R.surgeOf(b, side);
  const max = R.surgeMax();
  const nextAt = s.stage >= max ? null : R.surgeThresholds(b, side)[s.stage];
  return {
    label: CFG.surge.label,
    stage: s.stage,
    stageName: surgeStageName(s.stage),
    maxStage: max,
    points: s.points,
    nextAt,
    toNext: nextAt == null ? null : Math.max(0, nextAt - s.points),
    effect: { ...R.surgeEffect(s.stage) },
    thresholds: R.surgeThresholds(b, side).slice(),
  };
}
/** 撃破ポイントの表（中立・ヒーロー・建物の点、ガーディアンの即上昇の段数、段階の必要ポイント） */
export const surgeRules = () => ({
  points: { ...CFG.surge.points },
  bossStages: CFG.surge.bossStages,
  thresholds: CFG.surge.thresholds.slice(),
  stages: CFG.surge.stages.map((x) => ({ ...x })),
  enabled: CFG.surge.enabled,
});

/** 段階ごとに解放される特性の一覧（キャラID・敵ヒーローの型 rival_<型> → 段階 → 特性）。表示用 */
export function surgeTraitTable(): Record<string, Record<number, { id: string; name: string; desc: string }[]>> {
  const out: Record<string, Record<number, { id: string; name: string; desc: string }[]>> = {};
  for (const [key, stages] of Object.entries(CFG.surge.traits))
    out[key] = Object.fromEntries(Object.entries(stages).map(([st, list]) => [Number(st), list.map((t) => ({ id: t.id, name: t.name, desc: t.desc }))]));
  return out;
}
/** ユニットの妖力の段階（ヒーローは 1..3、それ以外は 1）。参で覚醒の姿にするなど描画用。actor.surge と同じ */
export const unitSurgeStage = (b: Battle, id: number) => {
  const a = R.actorById(b, id);
  return a && a.kind === 'hero' ? (a.surge ?? 1) : 1;
};
/** ユニット（ヒーロー）の特性のキー（キャラID または rival_<型>）と、今までに解放された特性 */
export function unitSurgeTraits(b: Battle, id: number): { key: string | null; traits: { id: string; name: string; desc: string }[] } {
  const a = R.actorById(b, id);
  if (!a || a.kind !== 'hero') return { key: null, traits: [] };
  return { key: a.spec?.trait ?? null, traits: R.traitsUpTo(a.spec?.trait, a.surge ?? 1).map((t) => ({ id: t.id, name: t.name, desc: t.desc })) };
}

/** 術の陣（灯籠陣・狐火の陣）の今のマス（ついて回る陣は持ち主のまわり）。表示用 */
/** 倒れて復活を待つ武将：戻るマスと、自分の手番であと何回後に戻るか（0＝次の自分の手番までに戻る） */
export function respawnView(b: Battle): { id: number; side: Side; cell: number; turns: number }[] {
  const me = R.heroOf(b, 'blue');
  const iv = me ? R.interval(b, me) : CFG.tick;
  const t0 = me?.alive ? (b.current === me.id ? b.time + iv : me.nextAt) : b.time;
  return b.actors
    .filter((a) => a.kind === 'hero' && !a.alive && a.respawnAt != null)
    .map((a) => ({ id: a.id, side: a.side as Side, cell: R.returnPoint(b, a), turns: Math.max(0, Math.ceil((a.respawnAt! - t0) / Math.max(1, iv))) }));
}
export function zonesView(b: Battle): { kind: 'lantern' | 'foxfire'; side: 'blue' | 'red'; cells: number[]; turns: number }[] {
  return (b.zones ?? []).map((z) => ({ kind: z.kind, side: z.side, cells: R.zoneCells(b, z), turns: z.turns }));
}
